import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../layouts/DashboardLayout';
import PageHeader from '../components/layout/PageHeader';
import { productsApi, qrApi } from '../lib/api';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { QrCode, Copy, CircleNotch, Check, Fingerprint, ShieldCheck } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { formatApiErrorDetail } from '../lib/utils';

export default function GenerateQRPage() {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [generationMode, setGenerationMode] = useState('linked');
  const [preprintScope, setPreprintScope] = useState('intended');
  const [batchNumber, setBatchNumber] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [generatedQRs, setGeneratedQRs] = useState([]);
  const [masterQR, setMasterQR] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const loadProducts = useCallback(async () => {
    try {
      const response = await productsApi.list();
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const isPreprint = generationMode === 'preprint';
  const isGenericPreprint = isPreprint && preprintScope === 'generic';
  const needsProduct = !isGenericPreprint;

  const handleGenerate = async () => {
    if (needsProduct && !selectedProduct) {
      toast.error(t('generateQr.chooseProd'));
      return;
    }

    setLoading(true);
    try {
      const response = await qrApi.generate(selectedProduct || null, quantity, {
        generation_mode: generationMode,
        preprint_scope: isPreprint ? preprintScope : undefined,
        batch_number: batchNumber.trim() || undefined,
      });
      setGeneratedQRs(response.data.qr_codes);
      setMasterQR(response.data.master_qr);
      if (isPreprint) {
        toast.success(t('generateQr.preprintSuccess', { count: response.data.count }));
      } else {
        toast.success(t('generateQr.linkedSuccess', { count: response.data.count }));
      }
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleCopyUrl = async (url, qrId) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(qrId);
      toast.success('URL copied to clipboard');
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const handleDownload = (qr) => {
    if (qr.qr_image) {
      // Download server-generated styled QR
      const link = document.createElement('a');
      link.href = `data:image/png;base64,${qr.qr_image}`;
      link.download = `qr-${qr.qr_id.substring(0, 8)}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const selectedProductData = products.find((p) => p.id === selectedProduct);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="generate-qr-page">
        <PageHeader
          title={t('generateQr.title')}
          description={t('generateQr.subtitle')}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Generator Form */}
          <Card className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'} shadow-sm`}>
            <CardHeader>
              <CardTitle className={`text-lg font-heading ${isDark ? 'text-zinc-100' : 'text-zinc-900'} flex items-center gap-2`}>
                <QrCode className="w-5 h-5 text-brand-500" />
                {t('generateQr.qrGenerator')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className={isDark ? 'text-zinc-300' : 'text-zinc-700'}>{t('generateQr.generationMode')}</Label>
                <div className="grid grid-cols-1 gap-2">
                  <button
                    type="button"
                    onClick={() => setGenerationMode('linked')}
                    className={`text-left rounded-lg border p-3 transition-colors ${
                      generationMode === 'linked'
                        ? isDark ? 'border-brand-500 bg-brand-500/10' : 'border-brand-500 bg-brand-50'
                        : isDark ? 'border-zinc-700 hover:border-zinc-600' : 'border-zinc-200 hover:border-zinc-300'
                    }`}
                    data-testid="mode-linked"
                  >
                    <span className={`block text-sm font-medium ${isDark ? 'text-zinc-100' : 'text-zinc-900'}`}>
                      {t('generateQr.modeLinked')}
                    </span>
                    <span className={`block text-xs mt-1 ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
                      {t('generateQr.modeLinkedDesc')}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setGenerationMode('preprint')}
                    className={`text-left rounded-lg border p-3 transition-colors ${
                      generationMode === 'preprint'
                        ? isDark ? 'border-brand-500 bg-brand-500/10' : 'border-brand-500 bg-brand-50'
                        : isDark ? 'border-zinc-700 hover:border-zinc-600' : 'border-zinc-200 hover:border-zinc-300'
                    }`}
                    data-testid="mode-preprint"
                  >
                    <span className={`block text-sm font-medium ${isDark ? 'text-zinc-100' : 'text-zinc-900'}`}>
                      {t('generateQr.modePreprint')}
                    </span>
                    <span className={`block text-xs mt-1 ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
                      {t('generateQr.modePreprintDesc')}
                    </span>
                  </button>
                </div>
              </div>

              {isPreprint && (
                <div className={`rounded-lg border p-3 space-y-2 ${isDark ? 'border-zinc-700 bg-zinc-800/30' : 'border-slate-200 bg-white'}`}>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preprintScope === 'generic'}
                      onChange={(e) => setPreprintScope(e.target.checked ? 'generic' : 'intended')}
                      className="mt-1"
                      data-testid="preprint-generic-stock"
                    />
                    <span>
                      <span className={`block text-sm font-medium ${isDark ? 'text-zinc-100' : 'text-zinc-900'}`}>
                        {t('generateQr.genericStock')}
                      </span>
                      <span className={`block text-xs mt-0.5 ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
                        {t('generateQr.genericStockDesc')}
                      </span>
                    </span>
                  </label>
                  <p className={`text-xs ${isDark ? 'text-brand-400' : 'text-brand-600'}`}>
                    {t('generateQr.preprintHint')}
                  </p>
                </div>
              )}

              {needsProduct && (
              <div className="space-y-2">
                <Label className={isDark ? 'text-zinc-300' : 'text-zinc-700'}>{t('generateQr.selectProduct')} *</Label>
                <Select value={selectedProduct} onValueChange={setSelectedProduct}>
                  <SelectTrigger className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : 'bg-white border-zinc-300 text-zinc-900'} data-testid="select-product">
                    <SelectValue placeholder={loadingProducts ? t('common.loading') : t('generateQr.chooseProd')} />
                  </SelectTrigger>
                  <SelectContent className={isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-zinc-200'}>
                    {products.map((product) => (
                      <SelectItem key={product.id} value={product.id} className={isDark ? 'text-zinc-100 focus:bg-zinc-700' : 'text-zinc-900 focus:bg-zinc-100'}>
                        {product.name} ({product.sku})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              )}

              {needsProduct && selectedProductData && (
                <div className={`p-3 rounded-md border ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-slate-50 border-slate-200'}`}>
                  <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>{t('generateQr.productDetails')}</p>
                  <p className={`font-medium mt-1 ${isDark ? 'text-zinc-100' : 'text-zinc-900'}`}>{selectedProductData.name}</p>
                  <p className={`text-sm mt-1 ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>SKU: {selectedProductData.sku}</p>
                  {selectedProductData.batch_number && (
                    <p className={`text-sm ${isDark ? 'text-brand-400' : 'text-brand-600'}`}>
                      {t('generateQr.batch')}: {selectedProductData.batch_number}
                    </p>
                  )}
                  {selectedProductData.scan_stages && (
                    <p className={`text-xs mt-1 ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
                      {selectedProductData.scan_stages.length} {t('generateQr.scanStages')}
                    </p>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label className={isDark ? 'text-zinc-300' : 'text-zinc-700'}>{t('generateQr.quantity')}</Label>
                <Input
                  type="number"
                  min={1}
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))}
                  className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : 'bg-white border-zinc-300 text-zinc-900'}
                  data-testid="quantity-input"
                />
              </div>

              {isPreprint && (
                <div className="space-y-2">
                  <Label className={isDark ? 'text-zinc-300' : 'text-zinc-700'}>{t('generateQr.batchNumberOptional')}</Label>
                  <Input
                    value={batchNumber}
                    onChange={(e) => setBatchNumber(e.target.value)}
                    placeholder="e.g. LOT-2026-001"
                    className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : 'bg-white border-zinc-300 text-zinc-900'}
                    data-testid="batch-number-input"
                  />
                </div>
              )}

              <Button
                onClick={handleGenerate}
                disabled={loading || (needsProduct && !selectedProduct)}
                className="w-full bg-brand-600 hover:bg-brand-700 text-white"
                data-testid="generate-qr-btn"
              >
                {loading ? (
                  <>
                    <CircleNotch className="w-4 h-4 mr-2 animate-spin" />
                    {t('generateQr.generating')}
                  </>
                ) : (
                  <>
                    <QrCode className="w-4 h-4 mr-2" />
                    {t('generateQr.generate')}
                  </>
                )}
              </Button>

              {/* Security info */}
              <div className={`p-3 rounded-md border ${isDark ? 'bg-brand-500/5 border-brand-500/20' : 'bg-brand-50 border-brand-200'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <ShieldCheck className={`w-4 h-4 ${isDark ? 'text-brand-400' : 'text-brand-600'}`} />
                  <span className={`text-xs font-medium ${isDark ? 'text-brand-400' : 'text-brand-600'}`}>{t('generateQr.tamperProof')}</span>
                </div>
                <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
                  {t('generateQr.securityInfo')}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Generated QR Codes */}
          <div className="lg:col-span-2">
            <Card className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'} shadow-sm`}>
              <CardHeader>
                <CardTitle className={`text-lg font-heading ${isDark ? 'text-zinc-100' : 'text-zinc-900'}`}>
                  {t('generateQr.generatedCodes')} ({generatedQRs.length}{masterQR ? ` + 1 ${t('generateQr.masterQr')}` : ''})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {generatedQRs.length === 0 && !masterQR ? (
                  <div className={`text-center py-12 ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
                    <QrCode className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>{t('generateQr.generateToSee')}</p>
                  </div>
                ) : (
                  <div className="space-y-4 max-h-[600px] overflow-y-auto">
                    {/* Master QR Card */}
                    {masterQR && (
                      <div className={`p-4 rounded-lg border-2 ${isDark ? 'bg-brand-900/20 border-brand-500/40' : 'bg-brand-50 border-brand-300'}`}
                        data-testid="master-qr-card">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="px-2 py-0.5 rounded text-xs font-bold bg-brand-600 text-white">{t('generateQr.masterQr')}</span>
                          <span className={`text-sm ${isDark ? 'text-zinc-300' : 'text-zinc-600'}`}>
                            {t('generateQr.packageBox')} - {masterQR.child_count} {t('generateQr.items')}
                          </span>
                        </div>
                        <div className="flex gap-4 items-start">
                          <div className="bg-white p-3 rounded-md border flex-shrink-0">
                            {masterQR.qr_image && (
                              <img src={`data:image/png;base64,${masterQR.qr_image}`} alt="Master QR"
                                className="w-32 h-32 object-contain" data-testid="master-qr-image" />
                            )}
                          </div>
                          <div className="flex-1 space-y-2">
                            <div>
                              <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>{t('generateQr.masterQrId')}</p>
                              <p className={`text-sm font-mono ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>{masterQR.qr_id}</p>
                            </div>
                            <div>
                              <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>{t('generateQr.batchId')}</p>
                              <p className={`text-sm font-mono ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>{masterQR.batch_id}</p>
                            </div>
                            <p className={`text-xs ${isDark ? 'text-brand-400' : 'text-brand-600'}`}>
                              {t('generateQr.attachBox')} {masterQR.child_count} {t('generateQr.items')}.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Individual QR codes */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {generatedQRs.map((qr) => (
                        <div key={qr.qr_id}
                          className={`p-4 rounded-lg border ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-slate-50 border-slate-200'}`}
                          data-testid={`qr-card-${qr.qr_id}`}>
                          <div className={`flex justify-center mb-4 p-4 rounded-md border ${isDark ? 'bg-white' : 'bg-white border-slate-200'}`}>
                            {qr.qr_image ? (
                              <img src={`data:image/png;base64,${qr.qr_image}`} alt={`QR Code ${qr.qr_id}`}
                                className="w-40 h-40 object-contain" data-testid={`qr-image-${qr.qr_id}`} />
                            ) : (
                              <div className="w-40 h-40 flex items-center justify-center text-zinc-400">
                                <QrCode className="w-20 h-20" />
                              </div>
                            )}
                          </div>
                          <div className="space-y-2">
                            <div>
                              <p className={`text-xs uppercase tracking-wider ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>QR ID</p>
                              <p className={`text-sm font-mono truncate ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>{qr.qr_id}</p>
                            </div>
                            <div>
                              <p className={`text-xs uppercase tracking-wider ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>{t('generateQr.publicUrl')}</p>
                              <p className="text-sm text-brand-500 truncate">{qr.public_url}</p>
                            </div>
                            {qr.fingerprint && (
                              <div className="flex items-center gap-1.5">
                                <Fingerprint className={`w-3.5 h-3.5 ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`} />
                                <p className={`text-xs font-mono ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>{qr.fingerprint}</p>
                              </div>
                            )}
                            <div className="flex gap-2 pt-2">
                              <Button size="sm" variant="outline" onClick={() => handleCopyUrl(qr.public_url, qr.qr_id)}
                                className={`flex-1 ${isDark ? 'border-zinc-700 text-zinc-300 hover:bg-zinc-700' : 'border-zinc-300 text-zinc-700 hover:bg-zinc-100'}`}
                                data-testid={`copy-url-${qr.qr_id}`}>
                                {copiedId === qr.qr_id ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                                {t('common.copyUrl')}
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

